// front/src/components/ProductCard/ProductCard.jsx
import './ProductCard.css';
import { Link } from 'react-router-dom';
import { useRef, useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';

export default function ProductCard({ product, role = 'guest' }) {
  const { add } = useCart();
  const { has, toggle } = useFavorites();

  const pid = String(product._id);

  // картинки (главная первой)
  const imgs = (product.images || []).map(i => i.url).filter(Boolean);
  const primaryIdx = Math.max(0, (product.images || []).findIndex(i => i?.isPrimary));
  if (primaryIdx > 0) {
    const [p] = imgs.splice(primaryIdx, 1);
    imgs.unshift(p);
  }

  // скраб по фото
  const [idx, setIdx] = useState(0);
  const thumbRef = useRef(null);
  const rafRef = useRef(0);

  useEffect(() => {
    const next = imgs[idx + 1], prev = imgs[idx - 1];
    [next, prev].forEach(src => { if (!src) return; const im = new Image(); im.src = src; });
  }, [idx, imgs]);

  const onMove = (clientX) => {
    const el = thumbRef.current;
    if (!el || imgs.length < 2) return;
    const r = el.getBoundingClientRect();
    const x = Math.min(Math.max(clientX - r.left, 0), r.width);
    const next = Math.floor((x / r.width) * imgs.length);
    if (next !== idx) setIdx(next);
  };

  const handleMouseMove = (e) => {
    if (rafRef.current) return;
    rafRef.current = requestAnimationFrame(() => {
      onMove(e.clientX);
      rafRef.current = 0;
    });
  };

  const [touchX, setTouchX] = useState(null);
  const onTouchStart = (e) => setTouchX(e.touches[0].clientX);
  const onTouchMove = (e) => {
    if (touchX == null) return;
    const dx = e.touches[0].clientX - touchX;
    if (Math.abs(dx) > 24) {
      setIdx(i => Math.max(0, Math.min(imgs.length - 1, i + (dx < 0 ? 1 : -1))));
      setTouchX(e.touches[0].clientX);
    }
  };
  const onLeave = () => setIdx(0);

  const img = imgs[idx] || '';
  const fav = has(pid);

  const toNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const price = product.price || {};
  const retail = toNum(price.retail ?? product.priceRetail);
  const wholesale = toNum(
    price.wholesale ?? (['admin', 'manager'].includes(role) ? product.priceWholesale : null)
  );
  const effective = toNum(price.effective) ?? retail;
  const privileged = ['wholesale', 'admin', 'manager'].includes(role);
  const showWholesalePrices = privileged && wholesale != null;
  const optPrice = showWholesalePrices
    ? (role === 'wholesale' ? (effective ?? wholesale) : wholesale)
    : null;

  const availableQty = Math.max(
    0,
    Number(product.availableQty ?? (Number(product.stockQty || 0) - Number(product.reservedQty || 0)))
  );
  const availability = product.availability || availabilityFromQty(availableQty);
  const canAdd = availableQty > 0;

  return (
    <div className="p-card">
      <div
        className={`p-card__thumb ${imgs.length > 1 ? 'is-scrub' : ''}`}
        ref={thumbRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={onLeave}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
      >
        <Link to={`/p/${product.slug}`} className="p-card__thumb-link" aria-label={product.title}>
          {img ? <img src={img} alt={product.title} /> : <div className="p-card__noimg" />}
        </Link>

        {imgs.length > 1 && (
          <div className="p-card__dots">
            {imgs.map((_, i) => <span key={i} className={`p-card__dot ${i === idx ? 'is-active' : ''}`} />)}
          </div>
        )}

        <button
          className={`p-card__fav ${fav ? 'is-active' : ''}`}
          aria-label={fav ? 'Убрать из избранного' : 'В избранное'}
          onClick={(e) => { e.preventDefault(); toggle(pid); }}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 21s-6.7-4.1-9.2-7.4C1 11.5 2 8.5 4.5 7.3 6.3 6.4 8.4 6.8 9.8 8.2L12 10.4l2.2-2.2c1.4-1.4 3.5-1.8 5.3-.9 2.5 1.2 3.5 4.2 1.7 6.3C18.7 16.9 12 21 12 21z" />
          </svg>
        </button>
      </div>

      <div className="p-card__body">
        {product.brand && <div className="p-card__brand">{product.brand}</div>}

        <h3 className="p-card__title">
          <Link to={`/p/${product.slug}`}>{product.title}</Link>
        </h3>

        <div className={`p-card__price ${showWholesalePrices ? 'p-card__price--ws' : ''}`}>
          {showWholesalePrices ? (
            <>
              {retail != null && (
                <div className="p-card__price-row">
                  <span className="p-card__price-label">Розничная</span>
                  <span className={`p-card__price-val ${retail > optPrice ? 'is-old' : ''}`}>
                    {fmt(retail)} ₽
                  </span>
                </div>
              )}
              <div className="p-card__price-row p-card__price-row--main">
                <span className="p-card__price-label">Оптовая</span>
                <span className="p-card__price-val"><b>{fmt(optPrice)} ₽</b></span>
              </div>
            </>
          ) : (
            effective != null && <b>{fmt(effective)} ₽</b>
          )}
        </div>

        {availability?.label && (
          <div className={`avail avail--${availability.code}`}>{availability.label}</div>
        )}

        <div className="p-card__actions">
          <button
            className="p-card__add"
            disabled={!canAdd}
            title={canAdd ? 'Добавить в корзину' : 'Нет в наличии'}
            onClick={() => add(product, 1)}
          >
            {canAdd ? 'В корзину' : 'Нет в наличии'}
          </button>
        </div>
      </div>
    </div>
  );
}

/* helpers */
function fmt(n) { return typeof n === 'number' ? n.toLocaleString('ru-RU') : ''; }
function availabilityFromQty(q) {
  const n = Number(q) || 0;
  if (n <= 0) return { code: 'out', label: 'Нет в наличии' };
  if (n <= 5) return { code: 'low', label: 'В наличии: мало' };
  return { code: 'in', label: 'В наличии: много' };
}
